import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@hooks/useAuth";

import legacyMigrationService, {
  LegacyMigrationCandidate,
  LegacyMigrationError,
  LegacyMigrationExtraction,
  LegacyMigrationOutcome,
  LegacyMigrationStatus,
  LegacyMigrationTenant,
  LegacySource,
  toLegacyMigrationError
} from "./service";

/** Users per request: a batch answers well inside any gateway's patience. */
const BATCH_SIZE = 20;

/** Lock outcomes that leave the legacy login locked; any other code is a lock that did not happen. */
export const LOCKED_REASONS = new Set(["LEGACY_LOCKED", "LEGACY_ALREADY_LOCKED"]);

/**
 * Whether the lock can be asked for this user: they need a password they can
 * sign in to Identity with — or locking would leave them out of both systems.
 * CRM: the legacy password kept, or an Identity account already there. FLEET:
 * passwords never travel, so only someone already in Identity or already
 * signing in to FLEET through it.
 */
export const canLock = (candidate: LegacyMigrationCandidate, source: LegacySource): boolean => (
  source === "FLEET"
    ? candidate.inIdentity || candidate.signedInWithIdentity
    : candidate.inIdentity || candidate.passwordKept
);

/** Where the operator is. Local state: nothing about the run is kept on the server. */
export type TStep = "customer" | "reading" | "review" | "migrating" | "done";

export interface MigrationProgress {
  done: number;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  passwordsPreserved: number;
  resetEmailsSent: number;
  /** Topcon Legado passwords locked by this run. */
  legacyLocked: number;
  /** Users flagged for the lock whose password could not be locked. */
  legacyLockFailed: number;
}

const EMPTY_PROGRESS: MigrationProgress = {
  done: 0,
  total: 0,
  created: 0,
  updated: 0,
  skipped: 0,
  errors: 0,
  passwordsPreserved: 0,
  resetEmailsSent: 0,
  legacyLocked: 0,
  legacyLockFailed: 0
};

/** Adds (or removes) these ids to a set, returning a new one. */
const withIds = (current: Set<string>, legacyIds: string[], add: boolean): Set<string> => {
  const next = new Set(current);

  legacyIds.forEach((legacyId) => {
    if (add) next.add(legacyId);
    else next.delete(legacyId);
  });

  return next;
};

export interface LegacyMigrationState {
  step: TStep;
  /** Where the users come from; picking another reloads the customer list. */
  source: LegacySource;
  changeSource: (source: LegacySource) => void;
  status: LegacyMigrationStatus | null;
  tenants: LegacyMigrationTenant[];
  tenantsLoaded: boolean;
  extraction: LegacyMigrationExtraction | null;
  /** Legacy ids the operator wants to migrate. */
  selected: Set<string>;
  /** Legacy ids whose Topcon Legado password will be locked. */
  lockLegacy: Set<string>;
  progress: MigrationProgress;
  outcomes: LegacyMigrationOutcome[];
  stopping: boolean;
  loading: boolean;
  busy: boolean;
  error: LegacyMigrationError | null;
  startExtraction: (tenantId: string) => Promise<void>;
  toggle: (legacyId: string, selected: boolean) => void;
  /** Selects or unselects these users — the ones the review is showing — and nobody else. */
  toggleMany: (legacyIds: string[], selected: boolean) => void;
  toggleLock: (legacyId: string, lock: boolean) => void;
  /** Flags or unflags these users for the Topcon Legado lock, and nobody else. */
  toggleLockMany: (legacyIds: string[], lock: boolean) => void;
  migrate: () => Promise<void>;
  stop: () => void;
  leave: () => void;
}

/**
 * State of the legacy migration assistant.
 *
 * The migration runs here, in the browser: one extraction, then one request per
 * batch until the reviewed list is done. Closing the screen stops it, and
 * running a customer again is safe — provisioning matches users by e-mail, and
 * whoever is already in Identity starts unselected.
 */
export const useLegacyMigration = (): LegacyMigrationState => {

  const { getToken } = useAuth();

  const service = useMemo(() => legacyMigrationService(getToken), [getToken]);

  const [step, setStep] = useState<TStep>("customer");
  const [source, setSource] = useState<LegacySource>("CRM");
  const [status, setStatus] = useState<LegacyMigrationStatus | null>(null);
  const [tenants, setTenants] = useState<LegacyMigrationTenant[]>([]);
  const [tenantsLoaded, setTenantsLoaded] = useState<boolean>(false);
  const [extraction, setExtraction] = useState<LegacyMigrationExtraction | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lockLegacy, setLockLegacy] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<MigrationProgress>(EMPTY_PROGRESS);
  const [outcomes, setOutcomes] = useState<LegacyMigrationOutcome[]>([]);
  const [stopping, setStopping] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<LegacyMigrationError | null>(null);

  const mounted = useRef<boolean>(true);
  /** Read inside the batch loop, which a state value would not see change. */
  const stopRequested = useRef<boolean>(false);

  useEffect(() => () => { mounted.current = false; }, []);

  const startExtraction = useCallback(async (tenantId: string) => {
    setStep("reading");
    setExtraction(null);
    setOutcomes([]);
    setProgress(EMPTY_PROGRESS);
    setBusy(true);
    setError(null);

    try {
      const result = await service.extract(tenantId, source);

      if (!mounted.current) return;

      const eligible = result.candidates.filter((candidate) => candidate.eligible);

      setExtraction(result);

      // Whoever is already in Identity starts unselected: this is what makes
      // running a customer again pick up only what is left.
      setSelected(new Set(eligible.filter((candidate) => !candidate.inIdentity).map((candidate) => candidate.legacyId)));

      // Locked by default only for whoever can sign in to Identity right away and
      // uses nothing else of the legacy system. CRM: the kept password, and no
      // other legacy area (that user probably still needs the Topcon Legado).
      // FLEET: whoever already signs in through Identity.
      setLockLegacy(new Set(eligible
        .filter((candidate) => !candidate.legacyLocked && (result.source === "FLEET"
          ? candidate.signedInWithIdentity
          : candidate.passwordKept && candidate.otherLegacyAreas.length === 0))
        .map((candidate) => candidate.legacyId)));

      setStep("review");
    } catch (rejection) {
      if (!mounted.current) return;

      // The banner says what went wrong; going back to the list is all the operator can do.
      setError(toLegacyMigrationError(rejection));
      setStep("customer");
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [service, source]);

  const toggle = useCallback((legacyId: string, isSelected: boolean) => {
    setSelected((current) => withIds(current, [legacyId], isSelected));
  }, []);

  const toggleMany = useCallback((legacyIds: string[], isSelected: boolean) => {
    setSelected((current) => withIds(current, legacyIds, isSelected));
  }, []);

  const toggleLock = useCallback((legacyId: string, lock: boolean) => {
    setLockLegacy((current) => withIds(current, [legacyId], lock));
  }, []);

  const toggleLockMany = useCallback((legacyIds: string[], lock: boolean) => {
    setLockLegacy((current) => withIds(current, legacyIds, lock));
  }, []);

  const migrate = useCallback(async () => {
    if (!extraction) return;

    const queue = extraction.candidates.filter((candidate) => candidate.eligible && selected.has(candidate.legacyId));

    if (queue.length === 0) return;

    // A module the customer has not contracted is shown, faded, but never sent:
    // the API refuses the whole batch over one such module.
    const contracted = new Set(extraction.contractedModules
      .filter((module) => module.contracted)
      .map((module) => module.module));

    stopRequested.current = false;
    setStopping(false);
    setStep("migrating");
    setOutcomes([]);
    setProgress({ ...EMPTY_PROGRESS, total: queue.length });
    setError(null);
    setBusy(true);

    for (let start = 0; start < queue.length; start += BATCH_SIZE) {
      if (stopRequested.current || !mounted.current) break;

      const batch = queue.slice(start, start + BATCH_SIZE).map((candidate) => ({
        legacyId: candidate.legacyId,
        email: candidate.email,
        modules: candidate.modules.filter((module) => contracted.has(module)),
        lockLegacyPassword: lockLegacy.has(candidate.legacyId) && !candidate.legacyLocked
      }));

      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await service.provision(extraction.tenantId, extraction.source, batch);

        if (!mounted.current) return;

        setOutcomes((current) => [...current, ...result.outcomes]);
        setProgress((current) => ({
          ...current,
          done: current.done + result.outcomes.length,
          created: current.created + result.createdCount,
          updated: current.updated + result.updatedCount,
          skipped: current.skipped + result.skippedCount,
          errors: current.errors + result.errorCount,
          passwordsPreserved: current.passwordsPreserved + result.passwordsPreservedCount,
          resetEmailsSent: current.resetEmailsSent + result.resetEmailsSentCount,
          legacyLocked: current.legacyLocked + result.legacyLockedCount,
          legacyLockFailed: current.legacyLockFailed + result.outcomes.filter((outcome) => outcome.legacyLockReason
            && !LOCKED_REASONS.has(outcome.legacyLockReason)).length
        }));
      } catch (rejection) {
        if (!mounted.current) return;

        // A failed batch stops the loop: carrying on would pile more failures of
        // the same cause onto the customer's database.
        setError(toLegacyMigrationError(rejection));
        break;
      }
    }

    if (mounted.current) {
      setBusy(false);
      setStep("done");
    }
  }, [extraction, lockLegacy, selected, service]);

  const stop = useCallback(() => {
    stopRequested.current = true;
    setStopping(true);
  }, []);

  const leave = useCallback(() => {
    setError(null);
    setStep("customer");
    setExtraction(null);
    setLockLegacy(new Set());
    setOutcomes([]);
    setProgress(EMPTY_PROGRESS);
  }, []);

  const changeSource = useCallback((next: LegacySource) => {
    setError(null);
    setSource(next);
  }, []);

  // First load: configuration plus the tenant list.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const configuration = await service.getStatus();

        if (!cancelled && mounted.current) setStatus(configuration);
      } catch (rejection) {
        if (!cancelled && mounted.current) setError(toLegacyMigrationError(rejection));
      }
    };

    load();

    return () => { cancelled = true; };
  }, [service]);

  // The customers of the picked source: every tenant for the CRM, those with
  // FLEET connected in the Tenant Manager for FLEET.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setTenantsLoaded(false);

      try {
        const list = await service.listTenants(source);

        if (cancelled || !mounted.current) return;

        setTenants(list);
        setTenantsLoaded(true);
      } catch (rejection) {
        if (!cancelled && mounted.current) {
          setTenants([]);
          setError(toLegacyMigrationError(rejection));
        }
      } finally {
        if (!cancelled && mounted.current) setLoading(false);
      }
    };

    load();

    return () => { cancelled = true; };
  }, [service, source]);

  return {
    step,
    source,
    changeSource,
    status,
    tenants,
    tenantsLoaded,
    extraction,
    selected,
    lockLegacy,
    progress,
    outcomes,
    stopping,
    loading,
    busy,
    error,
    startExtraction,
    toggle,
    toggleMany,
    toggleLock,
    toggleLockMany,
    migrate,
    stop,
    leave
  };
};

export default useLegacyMigration;
